/**
 * Card — bordered container with optional header, variant styling, and icon.
 *
 *   <Card>
 *     <p>Simple content</p>
 *   </Card>
 *
 *   <Card variant="warning" icon={AlertTriangle} title="Invite pending" description="This user hasn't set up yet.">
 *     <p>Details here</p>
 *   </Card>
 *
 *   <Card title="Ledger" headingLevel={2} region actions={<Button label="Month" className="w-32" />}>
 *     <Table />
 *   </Card>
 *
 * `actions` sit top right of the header, beside the title and description:
 * the place for the controls that change what the card shows (a date range,
 * a Day / Week / Month grouping).
 */

import { useId, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Card as ShadCard,
  CardContent,
  CardAction,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { SEVERITY_SURFACE } from '../_styles';

export type CardVariant = 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'success' | 'warning' | 'info';

const VARIANT_STYLES: Record<CardVariant, string> = {
  default: '',
  outline: 'border-border bg-background',
  secondary: 'bg-secondary/50 ring-secondary/20 text-secondary-foreground',
  ghost: 'bg-transparent ring-0 shadow-none',
  destructive: `${SEVERITY_SURFACE.destructive.bg} ${SEVERITY_SURFACE.destructive.ring} ${SEVERITY_SURFACE.destructive.text}`,
  success: `${SEVERITY_SURFACE.success.bg} ${SEVERITY_SURFACE.success.ring} ${SEVERITY_SURFACE.success.text}`,
  warning: `${SEVERITY_SURFACE.warning.bg} ${SEVERITY_SURFACE.warning.ring} ${SEVERITY_SURFACE.warning.text}`,
  info: `${SEVERITY_SURFACE.info.bg} ${SEVERITY_SURFACE.info.ring} ${SEVERITY_SURFACE.info.text}`,
};

const TITLE_STYLES: Record<CardVariant, string> = {
  default: '',
  outline: '',
  secondary: 'text-secondary-foreground',
  ghost: '',
  destructive: SEVERITY_SURFACE.destructive.text,
  success: SEVERITY_SURFACE.success.text,
  warning: SEVERITY_SURFACE.warning.text,
  info: SEVERITY_SURFACE.info.text,
};

const DESCRIPTION_STYLES: Record<CardVariant, string> = {
  default: '',
  outline: '',
  secondary: 'text-secondary-foreground/80',
  ghost: '',
  destructive: SEVERITY_SURFACE.destructive.subtleText,
  success: SEVERITY_SURFACE.success.subtleText,
  warning: SEVERITY_SURFACE.warning.subtleText,
  info: SEVERITY_SURFACE.info.subtleText,
};

const ICON_STYLES: Record<CardVariant, string> = {
  default: 'text-foreground',
  outline: 'text-foreground',
  secondary: 'text-secondary-foreground',
  ghost: 'text-foreground',
  destructive: SEVERITY_SURFACE.destructive.subtleText,
  success: SEVERITY_SURFACE.success.subtleText,
  warning: SEVERITY_SURFACE.warning.subtleText,
  info: SEVERITY_SURFACE.info.subtleText,
};

export interface CardProps {
  children?: ReactNode;
  title?: string;
  description?: string;
  /** Visual variant matching button naming convention. */
  variant?: CardVariant;
  /** Icon rendered alongside the title. */
  icon?: LucideIcon;
  className?: string;
  /** Class applied to the inner content area. */
  contentClassName?: string;
  /** Controls rendered top right of the header, e.g. range or grouping buttons. */
  actions?: ReactNode;
  /** Expose the title as a heading of this level (the title is plain text otherwise). */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Make the card a landmark region named by its title. */
  region?: boolean;
}

export function Card({
  children,
  title,
  description,
  variant = 'default',
  icon: Icon,
  className,
  contentClassName,
  actions,
  headingLevel,
  region = false,
}: CardProps) {
  const titleId = useId();
  const hasHeader = !!(title || description || actions);
  const labelled = region && !!title;

  return (
    <ShadCard
      className={cn(VARIANT_STYLES[variant], className)}
      role={labelled ? 'region' : undefined}
      aria-labelledby={labelled ? titleId : undefined}
    >
      {hasHeader && (
        <CardHeader>
          {title && (
            <CardTitle
              id={titleId}
              role={headingLevel ? 'heading' : undefined}
              aria-level={headingLevel}
              className={cn('flex items-center gap-1.5', TITLE_STYLES[variant])}
            >
              {Icon && <Icon className={cn('size-4 shrink-0', ICON_STYLES[variant])} />}
              {title}
            </CardTitle>
          )}
          {description && (
            <CardDescription className={cn(DESCRIPTION_STYLES[variant])}>
              {description}
            </CardDescription>
          )}
          {actions && (
            <CardAction className="flex flex-wrap items-center justify-end gap-2">{actions}</CardAction>
          )}
        </CardHeader>
      )}
      {children && (
        <CardContent className={cn(contentClassName)}>
          {children}
        </CardContent>
      )}
    </ShadCard>
  );
}
